import React, { useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { Navbar, Footer } from '../components/Navbar';
import { Logo } from '../components/Logo';
import { api, formatApiError } from '../lib/api';
import { ChevronLeft, ChevronRight, Plus, Pencil } from 'lucide-react';
import { toast } from 'sonner';
import CompleteProfileGate from '../components/CompleteProfileGate';
import ImageEditor from '../components/ImageEditor';
import { PartialDateInput, formatPartialDate } from '../components/PartialDateInput';
import { DateSelect } from '../components/DateSelect';
import { AIWritingAssist } from '../components/AIWritingAssist';
import { focalPosition } from '../lib/photoFocal';

const todayISO = () => new Date().toISOString().slice(0, 10);
import { RelationshipSelector, resolveRelationship, isRelationshipMissing } from '../components/RelationshipSelector';

const EMPTY_ADMIN = { name: '', email: '', phone: '', relationship: '', relationship_other: '', permission_level: 'admin' };
const EMPTY_FAMILY = { name: '', relationship: '', relationship_other: '', birth_date: '', death_date: '', is_living: false, location: '', resting_place: '', notes: '' };

const STEPS = [
  { id: 1, label: 'Profile' },
  { id: 2, label: 'Life Journey' },
  { id: 3, label: 'Memories' },
  { id: 4, label: 'Family Admins' },
  { id: 5, label: 'Immediate Family' },
  { id: 6, label: 'Activate' },
];

export default function CreateMemorial() {
  const loc = useLocation();
  // iter97.10 — When the wizard is opened from a family-member tap on
  // another memorial, the source page passes prefill + linked_from in
  // navigation state. We seed the form with what we already know about
  // the person, and forward the linkage fields to the create POST so
  // the backend can wire the two memorials together.
  const linkedFrom = loc.state?.linked_from || null;
  const prefill = loc.state?.prefill || null;

  const [step, setStep] = useState(1);
  const [memorial, setMemorial] = useState(null);
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState(null);
  // iter85 — Image editor wiring. Holds the freshly chosen file before
  // the user lands on the modal; once they Save the edited Blob, we
  // promote it to the upload state.
  const [editorOpen, setEditorOpen] = useState(false);
  const [editorFile, setEditorFile] = useState(null);

  const [form, setForm] = useState({
    full_name: prefill?.full_name || '',
    date_of_birth: prefill?.date_of_birth || '',
    date_of_passing: prefill?.date_of_passing || '',
    birthplace: '',
    profile_photo_url: '', life_summary: '',
  });

  // Profile photo — selected locally in Step 1, uploaded to the new memorial
  // the moment it's created, then stored back on the memorial record.
  const [photoFile, setPhotoFile] = useState(null);
  const [photoPreview, setPhotoPreview] = useState('');

  const [events, setEvents] = useState([]);
  const [newEvent, setNewEvent] = useState({ category: 'school', title: '', description: '', start_date: '', end_date: '', location: '' });
  // Per-field error map for the "Add chapter" form. Silently ignoring a
  // missing title was leaving users staring at a button that "did
  // nothing" — now we surface exactly which field is the blocker.
  const [eventErrors, setEventErrors] = useState({});

  const [uploadStatus, setUploadStatus] = useState('');
  const [mediaList, setMediaList] = useState([]);

  const [admins, setAdmins] = useState([]);
  const [newAdmin, setNewAdmin] = useState(EMPTY_ADMIN);

  const [family, setFamily] = useState([]);
  const [newFam, setNewFam] = useState(EMPTY_FAMILY);

  // iter95.3 — Activation step state (privacy + plan + checkout).
  const [plans, setPlans] = useState([]);
  const [qrMode, setQrMode] = useState('public');
  const [qrPin, setQrPin] = useState('');
  const [promoCode, setPromoCode] = useState('');
  const [promoApplied, setPromoApplied] = useState(null);
  const [promoChecking, setPromoChecking] = useState(false);
  const [activating, setActivating] = useState(null);

  const nav = useNavigate();

  const BACKEND = process.env.REACT_APP_BACKEND_URL;

  const uploadProfilePhoto = async (memorialId) => {
    if (!photoFile) return null;
    const fd = new FormData();
    fd.append('file', photoFile);
    fd.append('visibility', 'public');
    fd.append('caption', 'Profile photo');
    const { data: media } = await api.post(`/memorials/${memorialId}/media`, fd, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
    const url = `${BACKEND}/api/media/${media.media_id}/file`;
    const { data: updated } = await api.patch(`/memorials/${memorialId}`, { profile_photo_url: url });
    return updated;
  };

  const createProfile = async () => {
    if (!form.full_name.trim()) { setErr('A name is needed to begin.'); return; }
    if (!photoFile && !form.profile_photo_url.trim()) {
      setErr('A photograph helps families remember. Please add one to begin.');
      return;
    }
    setSaving(true); setErr(null);
    try {
      const payload = { ...form };
      if (linkedFrom?.memorial_id && linkedFrom?.family_id) {
        payload.linked_from_memorial_id = linkedFrom.memorial_id;
        payload.linked_from_family_id = linkedFrom.family_id;
      }
      const { data } = await api.post('/memorials', payload);
      let m = data;
      if (photoFile) {
        try {
          const updated = await uploadProfilePhoto(m.memorial_id);
          if (updated) m = updated;
        } catch (upErr) {
          // Keep the memorial record but surface the upload error
          setErr(formatApiError(upErr, 'Photo upload failed — you can retry on the Profile step.'));
        }
      }
      setMemorial(m);
      setStep(2);
    } catch (e) { setErr(formatApiError(e)); } finally { setSaving(false); }
  };

  const updateProfile = async () => {
    setSaving(true); setErr(null);
    try {
      let m;
      if (photoFile && memorial) {
        const updated = await uploadProfilePhoto(memorial.memorial_id);
        if (updated) m = updated;
      }
      const { data } = await api.patch(`/memorials/${memorial.memorial_id}`, form);
      setMemorial(m || data);
      setPhotoFile(null);
    } catch (e) { setErr(formatApiError(e)); } finally { setSaving(false); }
  };

  const addEvent = async () => {
    // Validate first — show inline red errors instead of silently doing nothing.
    const errs = {};
    if (!newEvent.title.trim()) {
      errs.title = 'A title is required — write a short phrase like "Married Eleanor" or "Graduated Penn State".';
    }
    if (Object.keys(errs).length > 0) {
      setEventErrors(errs);
      toast.error('Please fill in the highlighted field to add this chapter.');
      // Focus the first errored input so screen readers + keyboard users
      // know exactly where the problem is.
      setTimeout(() => {
        const first = document.querySelector('[data-testid="wizard-event-title-input"]');
        if (first) { first.focus(); first.scrollIntoView({ block: 'center', behavior: 'smooth' }); }
      }, 0);
      return;
    }
    setEventErrors({});
    try {
      const { data } = await api.post(`/memorials/${memorial.memorial_id}/life-events`, newEvent);
      setEvents((p) => [...p, data]);
      setNewEvent({ category: 'school', title: '', description: '', start_date: '', end_date: '', location: '' });
      toast.success('Chapter added to their life journey.');
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const uploadFile = async (file, visibility = 'public', caption = '') => {
    // iter94.13 — Insert an optimistic row with a local object-URL so the
    // user sees the thumbnail *immediately* on selection instead of an
    // empty grey box. The row is replaced with the real backend doc once
    // the upload completes. Object URLs are revoked in cleanup below.
    const tempId = `pending-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
    const localUrl = URL.createObjectURL(file);
    const isVideo = (file.type || '').startsWith('video');
    setMediaList((p) => [
      {
        media_id: tempId,
        _pending: true,
        _localUrl: localUrl,
        visibility,
        kind: isVideo ? 'video' : 'image',
      },
      ...p,
    ]);
    setUploadStatus(`Uploading ${file.name}…`);
    try {
      const fd = new FormData();
      fd.append('file', file);
      fd.append('visibility', visibility);
      fd.append('caption', caption);
      const { data } = await api.post(`/memorials/${memorial.memorial_id}/media`, fd, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      // Swap the optimistic row with the server doc. Keep the local URL
      // around briefly so the swap is seamless — revoke 5s later.
      setMediaList((p) => p.map((row) => (row.media_id === tempId ? { ...data, _localUrl: localUrl } : row)));
      setTimeout(() => URL.revokeObjectURL(localUrl), 5000);
      setUploadStatus('');
    } catch (e) {
      // Roll back the optimistic row on failure so the user isn't left
      // with a phantom "uploading" thumbnail.
      setMediaList((p) => p.filter((row) => row.media_id !== tempId));
      URL.revokeObjectURL(localUrl);
      setUploadStatus('');
      toast.error(formatApiError(e));
    }
  };

  // iter95.5 — Change a media item's visibility after upload from the
  // wizard grid. Mirrors the MediaTab admin dropdown so users don't have
  // to leave the wizard to re-classify a photo as Family Vault or Private.
  const changeMediaVisibility = async (mediaId, nextVisibility) => {
    if (!mediaId || mediaId.toString().startsWith('pending-')) return;
    // Optimistic update so the pill flips immediately.
    setMediaList((p) => p.map((row) => (row.media_id === mediaId ? { ...row, visibility: nextVisibility } : row)));
    try {
      await api.patch(`/media/${mediaId}`, { visibility: nextVisibility });
      toast.success(`Moved to ${nextVisibility === 'vault' ? 'Family Vault' : nextVisibility.charAt(0).toUpperCase() + nextVisibility.slice(1)}`);
    } catch (e) {
      toast.error(formatApiError(e));
      // Reload to revert if the patch failed.
      try {
        const { data } = await api.get(`/memorials/${memorial.memorial_id}/media`);
        if (data?.media) setMediaList(data.media);
      } catch (_) {}
    }
  };

  const inviteAdmin = async () => {
    if (!newAdmin.name || !newAdmin.email) return;
    if (isRelationshipMissing(newAdmin.relationship, newAdmin.relationship_other)) {
      toast.error('Please choose a relationship (or type one when "Other" is selected).');
      return;
    }
    try {
      const payload = {
        name: newAdmin.name,
        email: newAdmin.email,
        phone: newAdmin.phone,
        permission_level: newAdmin.permission_level,
        relationship: resolveRelationship(newAdmin.relationship, newAdmin.relationship_other),
      };
      const { data } = await api.post(`/memorials/${memorial.memorial_id}/admins`, payload);
      setAdmins((p) => [...p, data]);
      setNewAdmin(EMPTY_ADMIN);
      toast.success(
        data?.delivery?.delivered
          ? 'Invitation sent. Ask them to check their spam or junk folder if it is not in their inbox within a few minutes.'
          : 'Admin added — ready for the next one',
      );
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const addFamily = async () => {
    if (!newFam.name) return;
    if (isRelationshipMissing(newFam.relationship, newFam.relationship_other)) {
      toast.error('Please choose a relationship (or type one when "Other" is selected).');
      return;
    }
    try {
      const payload = { ...newFam, relationship: resolveRelationship(newFam.relationship, newFam.relationship_other) };
      delete payload.relationship_other;
      if (payload.is_living) {
        payload.death_date = null;
        payload.resting_place = null;
      } else if (!payload.death_date) {
        payload.is_living = true;
        payload.resting_place = null;
      }
      const { data } = await api.post(`/memorials/${memorial.memorial_id}/family`, payload);
      setFamily((p) => [...p, data]);
      setNewFam(EMPTY_FAMILY);
      toast.success('Person added — ready for the next one');
    } catch (e) { toast.error(formatApiError(e)); }
  };

  // iter95.3 — Activation step: fetch plans on first entry to step 6,
  // sync local QR mode/pin state with the saved memorial.
  React.useEffect(() => {
    if (step !== 6) return;
    api.get('/packages')
      .then(({ data }) => setPlans(Array.isArray(data?.packages) ? data.packages : []))
      .catch(() => setPlans([]));
    if (memorial) {
      setQrMode(memorial.qr_access_mode || 'public');
      setQrPin(memorial.qr_pin || '');
    }
  }, [step, memorial]);

  const applyPromo = async () => {
    if (!promoCode.trim()) return;
    setPromoChecking(true);
    try {
      const { data } = await api.post('/promo/validate', { code: promoCode.trim() });
      if (data.valid) {
        setPromoApplied({ code: data.code, discount_percent: data.discount_percent });
        toast.success(`${data.discount_percent}% off applied`);
      } else {
        setPromoApplied(null);
        toast.error('That code is not valid or has expired.');
      }
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setPromoChecking(false);
    }
  };

  const savePrivacyOnly = async () => {
    if (!memorial) return true;
    if (qrMode === 'private' && (qrPin || '').length < 4) {
      toast.error('Private QR needs a PIN of at least 4 characters.');
      return false;
    }
    try {
      // iter95.6 — qr_pin has min_length=4 on the backend, so we must send
      // `null` (not '') when the memorial is being set back to public mode.
      const body = { qr_access_mode: qrMode, qr_pin: qrMode === 'private' ? qrPin : null };
      const { data } = await api.patch(`/memorials/${memorial.memorial_id}`, body);
      setMemorial(data);
      return true;
    } catch (e) {
      toast.error(formatApiError(e));
      return false;
    }
  };

  const activateAndPay = async (planId) => {
    if (!memorial) return;
    setActivating(planId);
    try {
      const ok = await savePrivacyOnly();
      if (!ok) { setActivating(null); return; }
      const payload = {
        plan_id: planId,
        billing_cycle: 'annual',
        origin_url: window.location.origin,
        memorial_id: memorial.memorial_id,
      };
      if (promoApplied?.code) payload.promo_code = promoApplied.code;
      const { data } = await api.post('/billing/checkout', payload);
      window.location.href = data.url;
    } catch (e) {
      toast.error(formatApiError(e));
      setActivating(null);
    }
  };

  const saveDraftAndExit = async () => {
    await savePrivacyOnly();
    nav(`/dashboard/m/${memorial.memorial_id}?draft_saved=1`);
  };

  return (
    <div className="min-h-screen flex flex-col bg-background wizard-readable">
      <CompleteProfileGate />
      <Navbar />
      <main className="flex-1">
        <div className="ml-container-prose pt-16 sm:pt-24 pb-28">
          {/* Intro */}
          <div className="text-center">
            <h1 className="font-serif font-light text-4xl sm:text-5xl text-[hsl(var(--primary))] leading-[1.1]">
              Tell their story.
            </h1>
            <p className="ml-caption mt-8 max-w-md mx-auto">
              Take all the time you need. You can pause at any step and return whenever it feels right.
            </p>
          </div>

          {/* Serif-number step rail */}
          <div
            className="mt-16 flex items-start justify-center gap-8 sm:gap-12 overflow-x-auto pb-4"
            data-testid="wizard-progress"
            aria-label="Wizard progress"
          >
            {STEPS.map((s) => {
              const active = step === s.id;
              const done = step > s.id;
              return (
                <button
                  key={s.id}
                  onClick={() => (done || active) && setStep(s.id)}
                  className="shrink-0 text-center group focus:outline-none"
                  aria-current={active ? 'step' : undefined}
                  aria-label={`Step ${s.id}: ${s.label}`}
                >
                  <div
                    className={`ml-num-display text-lg transition-colors ${
                      active
                        ? 'text-[hsl(var(--primary))]'
                        : done
                        ? 'text-[hsl(var(--accent))]'
                        : 'text-[hsl(var(--primary)/0.3)]'
                    }`}
                  >
                    {s.id.toString().padStart(2, '0')}
                  </div>
                  <div
                    className={`mt-2 text-[11px] tracking-[0.22em] uppercase font-semibold transition-colors ${
                      active
                        ? 'text-[hsl(var(--primary))]'
                        : 'text-[hsl(var(--primary)/0.7)]'
                    }`}
                  >
                    {s.label}
                  </div>
                  <div
                    className={`mt-3 h-px w-10 mx-auto transition-colors ${
                      active
                        ? 'bg-[hsl(var(--accent))]'
                        : done
                        ? 'bg-[hsl(var(--accent)/0.45)]'
                        : 'bg-[hsl(var(--primary)/0.1)]'
                    }`}
                    aria-hidden
                  />
                </button>
              );
            })}
          </div>

          {/* Content — no boxed card, inputs on open cream */}
          <div className="mt-20 space-y-12">
            {err && (
              <div
                className="text-sm text-[hsl(var(--destructive))] border-l-2 border-[hsl(var(--destructive))] pl-4 font-light"
                role="alert"
                data-testid="wizard-error"
              >
                {err}
              </div>
            )}

            {/* Step 1 — Profile */}
            {step === 1 && (
              <section className="space-y-10" data-testid="wizard-step-1">
                {linkedFrom && !memorial && (
                  <div
                    className="rounded-2xl border border-[hsl(var(--accent)/0.45)] bg-[hsl(var(--accent)/0.08)] px-5 py-4 text-center"
                    data-testid="wizard-linked-from-banner"
                  >
                    <div className="text-[11px] tracking-[0.22em] uppercase font-semibold text-[hsl(var(--accent))]">
                      Linked memorial
                    </div>
                    <p className="mt-2 font-serif italic text-[hsl(var(--primary))] text-base sm:text-lg leading-relaxed">
                      Creating a memorial for <span className="font-medium not-italic">{prefill?.full_name || 'this person'}</span>
                      {linkedFrom.relationship ? <> &middot; <span className="not-italic">{linkedFrom.relationship}</span></> : null}
                      {linkedFrom.memorial_name ? <> from <span className="font-medium not-italic">{linkedFrom.memorial_name}</span></> : null}.
                    </p>
                    <p className="mt-1 text-xs text-[hsl(var(--primary)/0.65)]">
                      Once activated, both memorials will be linked so families can move between them.
                    </p>
                  </div>
                )}

                <StepHeader title="Who are we remembering?" caption="Begin with a name, a photograph, and the dates that bracket their life. That is enough." />

                {/* Photograph — the heart of the memorial */}
                <div className="flex flex-col items-center text-center">
                  <label
                    htmlFor="wizard-profile-photo"
                    className="relative cursor-pointer group focus-within:outline-none"
                    data-testid="wizard-profile-photo-picker"
                  >
                    <div className="w-36 h-36 sm:w-44 sm:h-44 rounded-full overflow-hidden bg-[hsl(var(--surface))] border border-[hsl(var(--primary)/0.12)] group-hover:border-[hsl(var(--accent))] transition-colors flex items-center justify-center">
                      {photoPreview || form.profile_photo_url ? (
                        <img
                          src={photoPreview || form.profile_photo_url}
                          alt="Preview"
                          className="w-full h-full object-cover"
                          data-testid="wizard-profile-photo-preview"
                        />
                      ) : (
                        <span className="font-serif italic text-[hsl(var(--primary)/0.7)] text-base px-4">
                          A photograph of them
                        </span>
                      )}
                    </div>
                    <div className="mt-4 text-[12px] tracking-[0.22em] uppercase font-semibold text-[hsl(var(--accent))] group-hover:text-[hsl(var(--primary))] transition-colors">
                      {photoFile || form.profile_photo_url ? 'Replace photograph' : 'Add a photograph'}
                    </div>
                    {(photoFile || form.profile_photo_url) && (
                      <button
                        type="button"
                        onClick={(e) => {
                          e.preventDefault();
                          if (photoFile) {
                            setEditorFile(photoFile);
                            setEditorOpen(true);
                          }
                        }}
                        disabled={!photoFile}
                        className="mt-2 inline-flex items-center gap-1 text-[11px] tracking-widest uppercase text-[hsl(var(--primary)/0.7)] hover:text-[hsl(var(--primary))] disabled:opacity-40 disabled:cursor-not-allowed"
                        data-testid="wizard-profile-photo-adjust"
                      >
                        <Pencil size={12} /> Adjust
                      </button>
                    )}
                    <input
                      id="wizard-profile-photo"
                      type="file"
                      accept="image/*"
                      className="hidden"
                      data-testid="wizard-profile-photo-input"
                      onChange={(e) => {
                        const f = e.target.files?.[0];
                        if (!f) return;
                        if (!f.type.startsWith('image/')) {
                          setErr('Please choose an image file.');
                          return;
                        }
                        // iter85 — open the editor instead of uploading raw.
                        setEditorFile(f);
                        setEditorOpen(true);
                        setErr(null);
                        // Reset native input so re-picking the same file works.
                        e.target.value = '';
                      }}
                    />
                  </label>
                  <p className="mt-3 text-sm text-[hsl(var(--primary)/0.78)] font-normal max-w-xs">
                    A clear, well-lit portrait works best. This photo appears on the memorial page, the QR plaque, and every story video.
                  </p>
                </div>

                <div className="grid sm:grid-cols-2 gap-x-10 gap-y-8">
                  <Field label="Full name"><input className="ml-input" value={form.full_name} onChange={(e) => setForm({ ...form, full_name: e.target.value })} data-testid="wizard-fullname" placeholder="Michael James Anderson" /></Field>
                  <Field label="Birthplace"><input className="ml-input" value={form.birthplace} onChange={(e) => setForm({ ...form, birthplace: e.target.value })} placeholder="Cork, Ireland" /></Field>
                  <Field label="Date of birth"><DateSelect value={form.date_of_birth || ''} max={form.date_of_passing || undefined} onChange={(v) => setForm({ ...form, date_of_birth: v })} testIdPrefix="wizard-dob" /></Field>
                  <Field label="Date of passing"><DateSelect value={form.date_of_passing || ''} min={form.date_of_birth || undefined} max={todayISO()} onChange={(v) => setForm({ ...form, date_of_passing: v })} testIdPrefix="wizard-dop" /></Field>
                </div>
                <Field label="A few words about them">
                  <textarea rows={5} className="ml-input resize-none leading-relaxed" value={form.life_summary || ''} onChange={(e) => setForm({ ...form, life_summary: e.target.value })} placeholder="A gentle paragraph — who they were, what they loved, how they were loved in return." />
                  <AIWritingAssist
                    context="life_summary"
                    deceasedName={form.full_name}
                    getCurrentText={() => form.life_summary || ''}
                    onInsert={(text) => setForm((f) => ({ ...f, life_summary: text }))}
                    testIdPrefix="wizard-ai-assist"
                  />
                </Field>
                <NavBtns onNext={memorial ? async () => { await updateProfile(); setStep(2); } : createProfile} loading={saving} />
              </section>
            )}

            {/* Step 2 — Life Journey */}
            {step === 2 && (
              <section className="space-y-10" data-testid="wizard-step-2">
                <StepHeader title="The chapters of their life." caption="Schools, marriages, children, the places they loved. Add as many, or as few, as you wish." />
                <div className="grid sm:grid-cols-2 gap-x-10 gap-y-8">
                  <Field label="Category">
                    <select className="ml-input" value={newEvent.category} onChange={(e) => setNewEvent({ ...newEvent, category: e.target.value })}>
                      {['school', 'work', 'marriage', 'child', 'grandchild', 'hobby', 'achievement', 'place', 'favorite', 'event'].map((c) => (
                        <option key={c} value={c}>{c.charAt(0).toUpperCase() + c.slice(1)}</option>
                      ))}
                    </select>
                  </Field>
                  <Field label="Title" required error={eventErrors.title}>
                    <input
                      className={`ml-input ${eventErrors.title ? 'ml-input--error' : ''}`}
                      placeholder="A one-line summary"
                      value={newEvent.title}
                      onChange={(e) => {
                        setNewEvent({ ...newEvent, title: e.target.value });
                        if (eventErrors.title) setEventErrors((p) => ({ ...p, title: undefined }));
                      }}
                      aria-invalid={!!eventErrors.title}
                      aria-describedby={eventErrors.title ? 'wizard-event-title-error' : undefined}
                      data-testid="wizard-event-title-input"
                    />
                  </Field>
                  <Field label="From (year)"><PartialDateInput value={newEvent.start_date} onChange={(v) => setNewEvent({ ...newEvent, start_date: v })} testIdPrefix="create-event-start" /></Field>
                  <Field label="Until (optional)"><PartialDateInput value={newEvent.end_date} onChange={(v) => setNewEvent({ ...newEvent, end_date: v })} testIdPrefix="create-event-end" /></Field>
                  <Field label="Place (optional)" full>
                    <input className="ml-input" placeholder="Where it happened" value={newEvent.location} onChange={(e) => setNewEvent({ ...newEvent, location: e.target.value })} />
                  </Field>
                  <Field label="Notes" full>
                    <textarea className="ml-input resize-none leading-relaxed" rows={3} placeholder="A sentence or two, in your own voice" value={newEvent.description} onChange={(e) => setNewEvent({ ...newEvent, description: e.target.value })} />
                  </Field>
                </div>
                <button onClick={addEvent} className="ml-btn-ghost" data-testid="wizard-add-event">
                  <Plus className="w-3 h-3" /> Add chapter
                </button>

                {events.length > 0 && (
                  <ul className="divide-y divide-[hsl(var(--primary)/0.08)] border-y border-[hsl(var(--primary)/0.08)]">
                    {events.map((ev) => (
                      <li key={ev.event_id} className="py-5 flex items-baseline gap-6">
                        <div className="ml-overline shrink-0 opacity-80">{ev.category}</div>
                        <div className="flex-1">
                          <div className="font-serif text-lg text-[hsl(var(--primary))]">{ev.title}</div>
                          <div className="text-xs text-[hsl(var(--primary)/0.55)] mt-1 tracking-wide">
                            {formatPartialDate(ev.start_date)}{ev.end_date ? ` — ${formatPartialDate(ev.end_date)}` : ''}{ev.location ? ` · ${ev.location}` : ''}
                          </div>
                        </div>
                      </li>
                    ))}
                  </ul>
                )}
                <NavBtns onBack={() => setStep(1)} onNext={() => setStep(3)} />
              </section>
            )}

            {/* Step 3 — Memories */}
            {step === 3 && (
              <section className="space-y-10" data-testid="wizard-step-3">
                <StepHeader title="Photographs and moving memories." caption="Each photograph or film chooses its own room — public, family vault, or private. You can change a photo's room any time by tapping the label on its thumbnail." />

                <div className="grid sm:grid-cols-3 gap-6">
                  {[
                    ['public', 'Public', 'Open to all who visit the memorial.'],
                    ['vault', 'Family Vault', 'For approved family only.'],
                    ['private', 'Private', 'Kept for admins alone.'],
                  ].map(([v, title, desc]) => (
                    <label
                      key={v}
                      className="flex flex-col items-center text-center px-6 py-8 border border-[hsl(var(--primary)/0.1)] hover:border-[hsl(var(--primary)/0.3)] cursor-pointer transition-colors"
                      data-testid={`wizard-upload-${v}`}
                    >
                      <div className="ml-overline">{title}</div>
                      <div className="font-serif text-xl mt-3 text-[hsl(var(--primary))]">Upload</div>
                      <div className="ml-divider-leaf w-12 my-5" aria-hidden />
                      <div className="text-sm text-[hsl(var(--primary)/0.78)] leading-relaxed font-normal">{desc}</div>
                      <input type="file" accept="image/*,video/*" className="hidden" onChange={(e) => {
                        const f = e.target.files?.[0];
                        if (f) uploadFile(f, v);
                        e.target.value = '';
                      }} />
                    </label>
                  ))}
                </div>

                {uploadStatus && <div className="text-xs tracking-widest uppercase text-[hsl(var(--primary)/0.6)]">{uploadStatus}</div>}

                {mediaList.length > 0 && (
                  <div className="grid grid-cols-3 sm:grid-cols-4 gap-4" data-testid="wizard-media-grid">
                    {mediaList.map((m) => {
                      // iter94.13 — Render the actual thumbnail.
                      // Priority: local object URL (instant, during upload)
                      // → external_url (cloud-hosted) → backend serve URL.
                      const src =
                        m._localUrl ||
                        m.external_url ||
                        (m.media_id && !m._pending ? `${BACKEND}/api/media/${m.media_id}/file` : null);
                      const isVideo = m.kind === 'video';
                      return (
                        <div
                          key={m.media_id}
                          className="relative aspect-square border border-[hsl(var(--primary)/0.1)] bg-[hsl(var(--muted))] overflow-hidden"
                          data-testid={`wizard-media-thumb-${m.media_id}`}
                        >
                          {src ? (
                            isVideo ? (
                              <video
                                src={src}
                                className="w-full h-full object-cover"
                                muted
                                playsInline
                                preload="metadata"
                              />
                            ) : (
                              <img
                                src={src}
                                alt={m.caption || 'Uploaded memory'}
                                className="w-full h-full object-cover"
                                style={{ objectPosition: focalPosition(m) }}
                                loading="lazy"
                              />
                            )
                          ) : (
                            <div className="absolute inset-0 flex items-center justify-center text-[hsl(var(--primary)/0.5)] font-serif text-sm">
                              {isVideo ? 'Film' : 'Photograph'}
                            </div>
                          )}
                          {m._pending && (
                            <div className="absolute inset-0 flex items-end p-1.5 bg-gradient-to-t from-black/55 via-transparent to-transparent text-white">
                              <span className="text-[9px] tracking-[0.22em] uppercase font-semibold">Uploading…</span>
                            </div>
                          )}
                          {m._pending ? (
                            <span
                              className="absolute top-1.5 right-1.5 text-[9px] tracking-[0.18em] uppercase font-semibold px-1.5 py-0.5 rounded-sm bg-black/55 text-white"
                              data-testid={`wizard-media-visibility-pill-${m.media_id}`}
                            >
                              {m.visibility}
                            </span>
                          ) : (
                            <select
                              value={m.visibility}
                              onChange={(e) => changeMediaVisibility(m.media_id, e.target.value)}
                              onClick={(e) => e.stopPropagation()}
                              className="absolute top-1.5 right-1.5 text-[9px] tracking-[0.18em] uppercase font-semibold px-1.5 py-0.5 rounded-sm bg-black/65 hover:bg-black/85 text-white border-none cursor-pointer focus:outline-none focus:ring-1 focus:ring-[hsl(var(--accent))]"
                              aria-label="Change visibility"
                              data-testid={`wizard-media-visibility-${m.media_id}`}
                            >
                              <option value="public">Public</option>
                              <option value="vault">Vault</option>
                              <option value="private">Private</option>
                            </select>
                          )}
                        </div>
                      );
                    })}
                  </div>
                )}
                <NavBtns onBack={() => setStep(2)} onNext={() => setStep(4)} />
              </section>
            )}

            {/* Step 4 — Admins */}
            {step === 4 && (
              <section className="space-y-10" data-testid="wizard-step-4">
                <StepHeader title="Who carries the keys?" caption="Admins are immediate family only — son, daughter, father, mother, wife, husband, or spouse. Other relatives belong on the Legacy Tree." />
                <div className="grid sm:grid-cols-2 gap-x-10 gap-y-8">
                  <Field label="Admin Full Name"><input className="ml-input" placeholder="Their name" value={newAdmin.name} onChange={(e) => setNewAdmin({ ...newAdmin, name: e.target.value })} /></Field>
                  <Field label="Email"><input className="ml-input" placeholder="them@family.com" value={newAdmin.email} onChange={(e) => setNewAdmin({ ...newAdmin, email: e.target.value })} /></Field>
                  <Field label="Phone (optional)"><input className="ml-input" placeholder="+1…" value={newAdmin.phone} onChange={(e) => setNewAdmin({ ...newAdmin, phone: e.target.value })} /></Field>
                  <Field label="Relationship">
                    <RelationshipSelector
                      preset="admin"
                      value={newAdmin.relationship}
                      customValue={newAdmin.relationship_other}
                      onChange={(rel, other) => setNewAdmin({ ...newAdmin, relationship: rel, relationship_other: other })}
                      selectTestId="wizard-admin-relationship"
                      inputTestId="wizard-admin-relationship-other"
                    />
                  </Field>
                  <Field label="Permission" full>
                    <select className="ml-input" value={newAdmin.permission_level} onChange={(e) => setNewAdmin({ ...newAdmin, permission_level: e.target.value })}>
                      <option value="admin">Admin — full access</option>
                      <option value="contributor">Contributor — uploads only</option>
                    </select>
                  </Field>
                </div>
                <button onClick={inviteAdmin} className="ml-btn-ghost" data-testid="wizard-invite-admin">
                  <Plus className="w-3 h-3" /> Send a quiet invite
                </button>
                {admins.length > 0 && (
                  <ul className="divide-y divide-[hsl(var(--primary)/0.08)] border-y border-[hsl(var(--primary)/0.08)]">
                    {admins.map((a) => (
                      <li key={a.admin_id} className="py-5 flex items-baseline gap-6">
                        <div className="font-serif text-lg text-[hsl(var(--primary))]">{a.name}</div>
                        <div className="text-xs tracking-[0.18em] uppercase text-[hsl(var(--primary)/0.55)]">{a.relationship}</div>
                        <div className="ml-auto text-[10px] tracking-[0.22em] uppercase text-[hsl(var(--accent))]">{a.status}</div>
                      </li>
                    ))}
                  </ul>
                )}
                <NavBtns onBack={() => setStep(3)} onNext={() => setStep(5)} />
              </section>
            )}

            {/* Step 5 — Immediate Family */}
            {step === 5 && (
              <section className="space-y-10" data-testid="wizard-step-5">
                <StepHeader title="The people closest to them." caption="Link the closest family now. Extended relatives, in-laws, and friends can join the Legacy Tree whenever you&rsquo;re ready." />
                <div className="grid sm:grid-cols-2 gap-x-10 gap-y-8">
                  <Field label="Name"><input className="ml-input" placeholder="Their name" value={newFam.name} onChange={(e) => setNewFam({ ...newFam, name: e.target.value })} /></Field>
                  <Field label="Relationship">
                    <RelationshipSelector
                      preset="immediate"
                      value={newFam.relationship}
                      customValue={newFam.relationship_other}
                      onChange={(rel, other) => setNewFam({ ...newFam, relationship: rel, relationship_other: other })}
                      selectTestId="wizard-family-relationship"
                      inputTestId="wizard-family-relationship-other"
                    />
                  </Field>
                  <Field label="Date of birth"><input type="date" className="ml-input" value={newFam.birth_date} onChange={(e) => setNewFam({ ...newFam, birth_date: e.target.value })} /></Field>
                  <Field label="Date of passing">
                    <div className="flex items-center gap-3">
                      <input
                        type="date"
                        className="ml-input flex-1"
                        value={newFam.death_date}
                        disabled={newFam.is_living}
                        onChange={(e) => setNewFam({ ...newFam, death_date: e.target.value })}
                        data-testid="wizard-family-death"
                      />
                      <label className="inline-flex items-center gap-2 cursor-pointer select-none text-[10px] tracking-[0.18em] uppercase text-[hsl(var(--primary)/0.7)] whitespace-nowrap">
                        <input
                          type="checkbox"
                          className="w-4 h-4 accent-[hsl(var(--secondary))]"
                          checked={newFam.is_living}
                          onChange={(e) =>
                            setNewFam({
                              ...newFam,
                              is_living: e.target.checked,
                              death_date: e.target.checked ? '' : newFam.death_date,
                              resting_place: e.target.checked ? '' : newFam.resting_place,
                            })
                          }
                          data-testid="wizard-family-living"
                        />
                        Still living
                      </label>
                    </div>
                  </Field>
                  <Field label={newFam.is_living || !newFam.death_date ? 'Location (where they live)' : 'Last known location (optional)'}>
                    <input
                      className="ml-input"
                      placeholder={newFam.is_living || !newFam.death_date ? 'e.g. Savannah, GA' : 'e.g. Savannah, GA'}
                      value={newFam.location}
                      onChange={(e) => setNewFam({ ...newFam, location: e.target.value })}
                      data-testid="wizard-family-location"
                    />
                  </Field>
                  {!newFam.is_living && newFam.death_date && (
                    <Field label="Resting place / cemetery">
                      <input
                        className="ml-input"
                        placeholder="e.g. Bonaventure Cemetery, Savannah, GA"
                        value={newFam.resting_place}
                        onChange={(e) => setNewFam({ ...newFam, resting_place: e.target.value })}
                        data-testid="wizard-family-resting-place"
                      />
                    </Field>
                  )}
                </div>
                <button onClick={addFamily} className="ml-btn-ghost" data-testid="wizard-add-family">
                  <Plus className="w-3 h-3" /> Add family member
                </button>
                {family.length > 0 && (
                  <ul className="divide-y divide-[hsl(var(--primary)/0.08)] border-y border-[hsl(var(--primary)/0.08)]">
                    {family.map((f) => (
                      <li key={f.family_id} className="py-5 flex items-center gap-6">
                        <span className={`w-2 h-2 rounded-full ${f.is_living ? 'bg-[hsl(var(--secondary))]' : 'bg-[hsl(var(--accent))]'}`} />
                        <div className="font-serif text-lg text-[hsl(var(--primary))]">{f.name}</div>
                        <div className="text-xs tracking-[0.18em] uppercase text-[hsl(var(--primary)/0.55)]">{f.relationship}</div>
                      </li>
                    ))}
                  </ul>
                )}
                <p className="ml-caption text-[hsl(var(--primary)/0.55)] text-sm">
                  After the wizard, the full Legacy Tree opens — spouses, grandchildren, in-laws, and any relative you wish to remember.
                </p>
                <NavBtns onBack={() => setStep(4)} onNext={() => setStep(6)} />
              </section>
            )}

            {/* Step 6 — Privacy + Plan + Pay (iter95.3) */}
            {step === 6 && (
              <section className="space-y-12" data-testid="wizard-step-6">
                <StepHeader
                  title="Activate this memorial."
                  caption="Choose how visitors find them, then pick a plan to publish the page, unlock the QR, and order plaques."
                />

                {/* Privacy block */}
                <div className="space-y-5" data-testid="wizard-step-6-privacy">
                  <div className="ml-overline">Who can scan and see this memorial?</div>
                  <div className="grid sm:grid-cols-2 gap-4">
                    {[
                      { id: 'public', label: 'Public', desc: 'Anyone who scans the QR sees the full memorial. The Family Vault stays private.' },
                      { id: 'private', label: 'Private', desc: 'Visitors must enter a PIN to view. They can still request family access.' },
                    ].map((opt) => (
                      <label
                        key={opt.id}
                        className={`flex gap-3 p-4 rounded-sm border cursor-pointer transition-colors ${
                          qrMode === opt.id
                            ? 'border-[hsl(var(--accent))] bg-[hsl(var(--accent))]/5'
                            : 'border-[hsl(var(--primary)/0.12)] hover:border-[hsl(var(--primary)/0.3)]'
                        }`}
                        data-testid={`wizard-qr-mode-${opt.id}`}
                      >
                        <input
                          type="radio"
                          name="wizard-qr-mode"
                          checked={qrMode === opt.id}
                          onChange={() => setQrMode(opt.id)}
                          className="mt-1"
                        />
                        <div>
                          <div className="font-medium text-[hsl(var(--primary))]">{opt.label}</div>
                          <div className="text-xs text-[hsl(var(--muted-foreground))] mt-1 leading-relaxed">{opt.desc}</div>
                        </div>
                      </label>
                    ))}
                  </div>
                  {qrMode === 'private' && (
                    <Field label="Memorial PIN (4–12 characters)">
                      <input
                        type="text"
                        className="ml-input max-w-xs"
                        value={qrPin}
                        onChange={(e) => setQrPin(e.target.value.replace(/\s/g, '').slice(0, 12))}
                        placeholder="e.g. 4827"
                        data-testid="wizard-qr-pin"
                      />
                    </Field>
                  )}
                </div>

                <div className="ml-divider-leaf w-12 mx-auto" aria-hidden />

                {/* Plan picker */}
                <div className="space-y-6" data-testid="wizard-step-6-plans">
                  <div className="text-center">
                    <div className="ml-overline">Choose a plan</div>
                    <h3 className="font-serif text-2xl text-[hsl(var(--primary))] mt-2">
                      Activate {memorial?.full_name || 'this memorial'}
                    </h3>
                    <p className="ml-caption text-sm mt-2 max-w-md mx-auto">
                      One-time + $12/year keeping fee. Your card is billed once at checkout, then the keeping fee renews each year.
                    </p>
                  </div>

                  <div className="grid sm:grid-cols-2 gap-5">
                    {plans.map((p) => {
                      const setup = p.price_setup || 0;
                      const annual = p.price_annual || 0;
                      // iter97.2 — 100% promo waives BOTH setup AND keeping
                      // fee for the first year. Partial promos (e.g. 50%)
                      // still only discount the setup fee.
                      const pct = promoApplied?.discount_percent || 0;
                      const isFull = pct >= 100;
                      const setupAfterPromo = pct
                        ? Math.round(setup * 100 * (1 - pct / 100)) / 100
                        : setup;
                      const annualAfterPromo = isFull ? 0 : annual;
                      const todayTotal = setupAfterPromo + annualAfterPromo;
                      return (
                        <div
                          key={p.id}
                          className="ml-card flex flex-col"
                          data-testid={`wizard-plan-card-${p.id}`}
                        >
                          <div className="ml-overline">{p.id === 'memorial_plus' ? 'Forever Here' : 'Memorial'}</div>
                          <div className="font-serif text-3xl mt-2 text-[hsl(var(--primary))]">
                            ${setup}<span className="text-base font-normal text-[hsl(var(--primary)/0.55)]"> + ${annual}/yr</span>
                          </div>
                          <ul className="mt-4 space-y-2 text-sm text-[hsl(var(--primary)/0.85)] flex-1">
                            {(p.features || []).map((f) => (
                              <li key={f} className="flex gap-2"><span className="text-[hsl(var(--accent))]">✓</span><span>{f}</span></li>
                            ))}
                          </ul>
                          {promoApplied && (
                            <div className="mt-4 text-xs text-[hsl(var(--accent))] leading-relaxed" data-testid={`wizard-plan-discounted-${p.id}`}>
                              {pct}% off setup → ${setupAfterPromo.toFixed(2)} setup<br />
                              {isFull
                                ? <>+ $0/yr keeping fee (waived for first year) = <strong>${todayTotal.toFixed(2)} today</strong></>
                                : <>+ ${annualAfterPromo}/yr keeping fee (not discounted) = <strong>${todayTotal.toFixed(2)} today</strong></>
                              }
                            </div>
                          )}
                          <button
                            onClick={() => activateAndPay(p.id)}
                            disabled={!!activating}
                            className="ml-btn-gold mt-5"
                            data-testid={`wizard-pay-${p.id}`}
                          >
                            {activating === p.id ? 'Activating…' : `Activate for $${todayTotal.toFixed(2)}`}
                          </button>
                        </div>
                      );
                    })}
                  </div>

                  {/* Promo code */}
                  <div className="ml-card !p-4 max-w-md mx-auto">
                    {promoApplied ? (
                      <div className="flex items-center justify-between gap-3">
                        <div className="text-sm">
                          <strong className="text-[hsl(var(--accent))]">{promoApplied.code}</strong> · {promoApplied.discount_percent}% off <em className="text-[hsl(var(--muted-foreground))]">setup fee only</em>
                        </div>
                        <button
                          onClick={() => { setPromoApplied(null); setPromoCode(''); }}
                          className="text-xs underline text-[hsl(var(--muted-foreground))]"
                          data-testid="wizard-promo-clear"
                        >
                          remove
                        </button>
                      </div>
                    ) : (
                      <div className="flex gap-2">
                        <input
                          type="text"
                          value={promoCode}
                          onChange={(e) => setPromoCode(e.target.value)}
                          className="ml-input flex-1"
                          placeholder="Promo code (optional)"
                          data-testid="wizard-promo-input"
                        />
                        <button
                          onClick={applyPromo}
                          disabled={promoChecking || !promoCode.trim()}
                          className="ml-btn-outline"
                          data-testid="wizard-promo-apply"
                        >
                          {promoChecking ? 'Checking…' : 'Apply'}
                        </button>
                      </div>
                    )}
                    <div className="mt-3 text-[11px] text-[hsl(var(--muted-foreground))] leading-relaxed">
                      Promo codes discount the one-time setup fee only. The $12/yr keeping fee that hosts the memorial isn&apos;t waived — it keeps your loved one&apos;s page alive year after year.
                    </div>
                  </div>
                </div>

                <div className="flex flex-wrap items-center justify-between gap-4 pt-4">
                  <button
                    onClick={() => setStep(5)}
                    className="ml-btn-link inline-flex items-center gap-2 text-sm text-[hsl(var(--muted-foreground))]"
                    data-testid="wizard-back-to-family"
                  >
                    ← Back
                  </button>
                  <button
                    onClick={saveDraftAndExit}
                    className="ml-btn-outline"
                    data-testid="wizard-save-draft-btn"
                  >
                    Save as draft — choose plan later
                  </button>
                </div>

                <p className="text-center text-[11px] tracking-[0.18em] uppercase text-[hsl(var(--primary)/0.45)]">
                  Until you activate, the public page, QR, and plaque ordering stay locked. Your draft is safe — pick this up any time.
                </p>
              </section>
            )}
          </div>
        </div>
      </main>
      <Footer />
      <ImageEditor
        open={editorOpen}
        file={editorFile}
        aspect={1}
        title="Adjust the photograph"
        saveLabel="Save photograph"
        onCancel={() => { setEditorOpen(false); setEditorFile(null); }}
        onSave={(edited) => {
          setEditorOpen(false);
          setPhotoFile(edited);
          setPhotoPreview(URL.createObjectURL(edited));
          setEditorFile(null);
        }}
      />
    </div>
  );
}

function StepHeader({ title, caption, centered }) {
  return (
    <header className={centered ? 'text-center' : ''}>
      <h2 className="font-serif font-light text-3xl sm:text-4xl text-[hsl(var(--primary))] leading-[1.15]">
        {title}
      </h2>
      {caption && (
        <p className={`ml-caption mt-6 ${centered ? 'max-w-md mx-auto' : 'max-w-lg'}`}>
          {caption}
        </p>
      )}
    </header>
  );
}

function Field({ label, children, full, required, error }) {
  return (
    <div className={full ? 'sm:col-span-2' : ''}>
      <label className="ml-label">
        {label}
        {required && <span className="text-red-600 ml-1" aria-hidden="true">*</span>}
      </label>
      {children}
      {error && (
        <p
          role="alert"
          className="mt-1 text-xs text-red-600 font-medium"
          data-testid="field-error"
        >
          {error}
        </p>
      )}
    </div>
  );
}

function NavBtns({ onBack, onNext, loading }) {
  return (
    <div className="flex justify-between items-center pt-10 border-t border-[hsl(var(--primary)/0.08)]">
      <div>
        {onBack && (
          <button onClick={onBack} className="ml-btn-ghost" data-testid="wizard-back-btn">
            <ChevronLeft className="w-3 h-3" /> Back
          </button>
        )}
      </div>
      <button onClick={onNext} disabled={loading} className="ml-btn-primary" data-testid="wizard-next-btn">
        {loading ? 'Saving…' : (<>Continue their story <ChevronRight className="w-3 h-3" /></>)}
      </button>
    </div>
  );
}
